<template>
  <div
    :style="{ height: computedHeight, width: menuWidth }"
    class="menu"
    :class="[{ 'menu--searchable': searchable }, { 'menu--full-width': fullWidth }]"
  >
    <UIInputSearch
      v-if="searchable"
      ref="searchRef"
      v-model="search"
      autocomplete="new"
      name="menu-search"
      :placeholder="searchPlaceholder"
      :compact
      sync-v-model
      :controlled="false"
    />
    <div ref="list" class="menu__list" :class="{ 'menu__list--no-results': shouldShowNoResults }">
      <div v-if="shouldShowNoResults" class="menu__no-result">
        {{ getResultText }}
      </div>
      <div v-if="customContent" class="menu__scroller">
        <slot name="custom" />
      </div>
      <div v-else class="menu__scroller" :prerender="10">
        <template v-if="filteredItems.length > 0">
          <slot
            v-for="item in filteredItems"
            :key="`${item.name}-${item[props.valueKey]}`"
            :active="getIsItemActive(item)"
            :item="item"
          />
        </template>
      </div>
      <div v-if="isLoadingMore && filteredItems.length" class="menu__loading-more">
        <UIProgressSpinner width="20" height="20" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual'
import type { MenuProps } from '../../../types/ui'

import type { UIInputSearch } from '#build/components'

const props = withDefaults(defineProps<MenuProps & {
  isLoadingMore?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
}>(), {
  noResultText: 'No results',
  searchPlaceholder: 'Search',
  valueKey: 'id',
  itemSize: 48,
  customContent: false,
  menuWidth: '300px',
  searchKey: () => ['name'],
  compact: false,
  isLoadingMore: false,
  hasMore: false,
  onLoadMore: undefined,
})

const itemSizePx = `${props.itemSize}px`

const searchHeight = props.compact ? 40 : 48
const searchHeightPx = `${searchHeight}px`
const basePadding = 4
const baseBorderWidth = 1.5
const listMarginTop = 4
const totalPaddingHeight = basePadding * 2
const totalBorderHeight = baseBorderWidth * 2

const defaultMenuHeight = computed(() => {
  return props.menuHeight ? Number(props.menuHeight?.replace('px', '')) : 300
})

defineSlots<{
  default(props: { item: any; active: boolean; key: any }): any
  custom(): any
}>()

const list = ref<HTMLDivElement | null>(null)

const search = ref('')
const searchRef = ref<InstanceType<typeof UIInputSearch> | null>(null)

const { filter } = useFilterItems()

const searchValue = computed(() => {
  return props.searchValue || search.value
})

const filteredItems = computed(() => {
  if (!props.items || !Array.isArray(props.items)) return []

  if (props.onLoadMore) {
    return props.items
  }

  return searchValue.value ? filter(props.items, searchValue.value, props.searchKey) : props.items
})

const shouldShowNoResults = computed(() => {
  if (props.customContent || props.loading || props.isLoadingMore) return false
  if (filteredItems.value.length > 0) return false
  
  if (props.onLoadMore) {
    if (!props.items) return false
    if (Array.isArray(props.items) && props.items.length > 0) return false
    if (searchValue.value) return true
    return false
  }
  
  return searchValue.value ? true : false
})

const getResultText = computed(() => {
  if (props.loading) {
    return 'Loading...'
  }

  if (filteredItems.value.length === 0) {
    if (props.onLoadMore) {
      if (props.items && props.items.length === 0 && searchValue.value) {
        return props.noResultText
      }
    } else if (searchValue.value) {
      return props.noResultText
    }
  }

  return 'No options'
})

const computedHeight = computed(() => {
  if (props.customContent) {
    return ''
  }

  if (shouldShowNoResults.value) {
    const noResultHeight = props.itemSize + totalPaddingHeight + totalBorderHeight
    if (props.searchable) {
      return `${noResultHeight + searchHeight + listMarginTop}px`
    }
    return `${noResultHeight}px`
  }

  if (props.menuHeight) {
    return props.menuHeight
  }

  const itemsHeight = filteredItems.value.length * props.itemSize

  if (props.searchable) {
    if (!filteredItems.value.length) {
      return `${props.itemSize + searchHeight + totalPaddingHeight + listMarginTop + totalBorderHeight}px`
    }

    const calculatedHeight = itemsHeight + totalPaddingHeight + totalBorderHeight + searchHeight + listMarginTop
    return `${Math.min(calculatedHeight, defaultMenuHeight.value)}px`
  }

  if (!filteredItems.value.length) {
    return `${props.itemSize + totalPaddingHeight + totalBorderHeight}px`
  }

  const calculatedHeight = itemsHeight + totalPaddingHeight + totalBorderHeight
  return `${Math.min(calculatedHeight, defaultMenuHeight.value)}px`
})

const getIsItemActive = (item: any) => {
  if (!props.modelValue) {
    return false
  }

  if (props.multiple) {
    return (props.modelValue as number[]).includes(item[props.valueKey])
  }

  // * Because props.modelValue is parsed from query params as string
  // eslint-disable-next-line eqeqeq
  return item[props.valueKey] == props.modelValue || isEqual(props.modelValue, item[props.valueKey])
}

watchEffect(() => {
  if (!props.menuVisible) {
    search.value = ''
  }
})

const handleScroll = () => {
  if (!list.value || !props.onLoadMore || !props.hasMore || props.isLoadingMore) return

  const scrollElement = list.value
  const scrollTop = scrollElement.scrollTop
  const scrollHeight = scrollElement.scrollHeight
  const clientHeight = scrollElement.clientHeight

  const threshold = 50

  if (scrollTop + clientHeight >= scrollHeight - threshold) {
    props.onLoadMore()
  }
}

onMounted(() => {
  if (list.value && props.onLoadMore) {
    list.value.addEventListener('scroll', handleScroll)
  }
})

onUnmounted(() => {
  if (list.value && props.onLoadMore) {
    list.value.removeEventListener('scroll', handleScroll)
  }
})

defineExpose({ list, searchRef })
</script>

<style scoped lang="scss">
@use '../../../styles/mixins.scss' as *;
@use '../../../styles/variables.scss' as *;

$base-padding: 4px;
$list-margin-top: 4px;

.menu {
  background: white;
  z-index: 100;
  box-shadow: $base-box-shadow;
  border-radius: $base-border-radius;
  border: $base-border-width solid $black-10;
  padding: $base-padding;
  width: 300px;

  &--searchable {
    .menu__list {
      margin-top: $list-margin-top;
      height: calc(100% - v-bind(searchHeightPx) - $list-margin-top);
    }
  }

  &--full-width {
    width: 100%;
  }

  &__scroller {
    height: 100%;
  }

  &__list {
    margin-top: 0;
    height: 100%;
    overflow-y: auto;
    position: relative;
    z-index: 100;

    @include baseScrollbar();

    &--no-results {
      overflow-y: hidden;
      max-height: v-bind(itemSizePx);
    }
  }

  &__no-result {
    font-size: 14px;
    color: $black-80;
    height: v-bind(itemSizePx);
    display: flex;
    align-items: center;
    padding-left: calc(16px - $base-padding);
    position: relative;
    width: 100%;
    flex-shrink: 0;
  }

  &__loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    min-height: 48px;
  }
}
</style>
